import { useState, useEffect, Suspense, lazy } from "react";

/**
 * Dynamic import factory — bundler can't statically analyze these because
 * the function is only called at runtime when the API tells us which name to use.
 * Each entry is a thunk; we only call the one we need.
 *
 * To add a new ad component:
 *  1. Add its name → () => import(...) entry here
 *  2. Add its size to COMPONENT_SIZES
 *  3. Add its props mapping to getComponentProps
 */
const IMPORT_MAP = {
  MatchHeadWidget: () => import("./MatchHeadWidget"),
  MatchTimeline: () => import("./MatchTimelineWidget"),
  MatchStatsWidget: () => import("./MatchStatsWidget"),
  FanVoteWidget: () => import("./FanVoteWidget"),
};

const COMPONENT_SIZES = {
  MatchHeadWidget: { width: 300, height: 250 },
  MatchTimeline: { width: 300, height: 600 },
  MatchStatsWidget: { width: 300, height: 600 },
  FanVoteWidget: { width: 280, height: 364 },
};

const getComponentProps = (
  name,
  componentData,
  difference,
  socketConfig = {},
) => {
  if (name === "MatchHeadWidget") {
    return {
      match: componentData?.data,
      difference: difference || "+0",
      loadCSS: false,
      socketUrl: socketConfig.socketUrl || "",
      channel: socketConfig.channel || "",
      event: socketConfig.event || "",
    };
  }
  if (name === "MatchTimeline") {
    return {
      data: componentData?.summary,
      matchDetail: componentData,
      loadCSS: false,
    };
  }
  if (name === "MatchStatsWidget") {
    return {
      data: componentData?.stats,
      matchDetail: componentData?.match,
      loadCSS: false,
    };
  }
  if (name === "FanVoteWidget") {
    return {
      question: componentData?.question,
      options: componentData?.options,
      category: componentData?.category,
      league: componentData?.league,
      emoji: componentData?.emoji,
      apiUrl: componentData?.apiUrl,
      loadCSS: false,
    };
  }
  return { loadCSS: false };
};

// Module-level cache: name → lazy component (created once, reused on re-renders)
const lazyCache = {};

const getLazy = (name) => {
  if (!lazyCache[name]) {
    const importer = IMPORT_MAP[name];
    if (!importer) return null;
    lazyCache[name] = lazy(importer);
  }
  return lazyCache[name];
};

/**
 * ComponentAdRenderer
 *
 * Renders a single ad component driven by the API response:
 *   { name: "MatchTimeline", componentData: { ... } }
 *
 * Bundle impact: only the component whose name arrives from the API is ever
 * fetched. The IMPORT_MAP thunks are not executed until getLazy(name) is called.
 */
const ComponentAdRenderer = ({
  name,
  componentData,
  difference = "+0",
  socketConfig = {},
  style = {},
}) => {
  const [Component, setComponent] = useState(() => getLazy(name));

  useEffect(() => {
    setComponent(() => getLazy(name));
  }, [name]);

  if (!Component) {
    console.warn(`[ComponentAdRenderer] Unknown component: "${name}"`);
    return null;
  }

  const { width, height } = COMPONENT_SIZES[name] || {
    width: 300,
    height: 300,
  };
  const compProps = getComponentProps(
    name,
    componentData,
    difference,
    socketConfig,
  );

  return (
    <div
      style={{ maxWidth: width, maxHeight: height, flexShrink: 0, ...style }}
    >
      <Suspense
        fallback={
          <div
            style={{ width, height, background: "#f5f5f5", borderRadius: 12 }}
          />
        }
      >
        <Component {...compProps} />
      </Suspense>
    </div>
  );
};

export default ComponentAdRenderer;
